import React, { useState } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  CleanTable,
  CleanTableBody,
  CleanTableCell,
  CleanTableHead,
  CleanTableHeader,
  CleanTableRow,
} from "../ui/clean-table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { Users, Search, Trash2, Filter, RefreshCw } from "lucide-react";

const UserTable = ({
  users,
  contextType,
  businessUnits = [],
  canManage,
  onRemoveUser,
  onRefresh,
  onAddUser,
  onInviteManagement,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [businessUnitFilter, setBusinessUnitFilter] = useState("all");
  const [filteredUsers, setFilteredUsers] = useState(users);

  // Filter users based on search, role, and business unit
  React.useEffect(() => {
    let filtered = users;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (user) =>
          user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.title?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply role filter
    if (roleFilter !== "all") {
      if (contextType === "organization") {
        filtered = filtered.filter((user) =>
          user.roles.some((role) => role.type === roleFilter)
        );
      } else {
        filtered = filtered.filter((user) => user.role === roleFilter);
      }
    }

    // Apply business unit filter (only for organization context)
    if (contextType === "organization" && businessUnitFilter !== "all") {
      filtered = filtered.filter((user) =>
        user.businessUnits.some((bu) => bu.id === businessUnitFilter)
      );
    }

    setFilteredUsers(filtered);
  }, [users, searchTerm, roleFilter, businessUnitFilter, contextType]);

  // Get user initials for avatar
  const getUserInitials = (name) => {
    return (
      name
        ?.split(" ")
        .map((word) => word.charAt(0))
        .join("")
        .toUpperCase()
        .slice(0, 2) || "?"
    );
  };

  // Get role badge variant
  const getRoleBadgeVariant = (roleType) => {
    switch (roleType) {
      case "org_admin":
        return "default";
      case "business_unit_head":
        return "default";
      case "business_user":
        return "secondary";
      default:
        return "secondary";
    }
  };

  // Get role display name
  const getRoleDisplayName = (roleType) => {
    switch (roleType) {
      case "org_admin":
        return "Organization Admin";
      case "business_unit_head":
        return "Business Unit Head";
      case "business_user":
        return "Business User";
      default:
        return roleType;
    }
  };

  if (users.length === 0) {
    return (
      <Card className="border-0 shadow-none">
        <CardContent>
          <div className="text-center py-12">
            <Users className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Users Found</h3>
            <p className="text-muted-foreground mb-4">
              No users have been added yet.
            </p>
            {canManage && (
              <div className="flex gap-3 justify-center">
                {onAddUser && (
                  <Button
                    size="sm"
                    onClick={onAddUser}
                    className="flex items-center gap-2"
                  >
                    Add User
                  </Button>
                )}
                {onInviteManagement && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onInviteManagement}
                    className="flex items-center gap-2"
                  >
                    Manage Invites
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-none">
      <CardContent>
        <div className="pt-6">
          {/* Search and Filters */}
          <div className="mb-6 space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users by name, email, or title..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="flex gap-4 flex-wrap">
              <div className="min-w-[200px]">
                <label className="text-sm font-medium text-muted-foreground mb-2 block flex items-center gap-2">
                  <Filter className="h-4 w-4" />
                  Filter by Role
                </label>
                <Select value={roleFilter} onValueChange={setRoleFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    {contextType === "organization" && (
                      <SelectItem value="org_admin">
                        Organization Admin
                      </SelectItem>
                    )}
                    <SelectItem value="business_unit_head">
                      Business Unit Head
                    </SelectItem>
                    <SelectItem value="business_user">Business User</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {contextType === "organization" && (
                <div className="min-w-[200px]">
                  <label className="text-sm font-medium text-muted-foreground mb-2 block flex items-center gap-2">
                    Filter by Business Unit
                  </label>
                  <Select
                    value={businessUnitFilter}
                    onValueChange={setBusinessUnitFilter}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select business unit" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Business Units</SelectItem>
                      {businessUnits.map((bu) => (
                        <SelectItem key={bu.id} value={bu.id}>
                          {bu.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {(roleFilter !== "all" || businessUnitFilter !== "all") && (
                <div className="flex align-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setRoleFilter("all");
                      setBusinessUnitFilter("all");
                    }}
                    className="mt-6"
                  >
                    Clear Filters
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Users Table */}
          {filteredUsers.length === 0 ? (
            <div className="text-center py-8">
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Users Found</h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm
                  ? "No users match your search criteria."
                  : "No users found for the selected filter."}
              </p>
              {searchTerm && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSearchTerm("")}
                >
                  Clear Search
                </Button>
              )}
            </div>
          ) : (
            <TooltipProvider>
              <CleanTable>
                <CleanTableHeader>
                  <CleanTableRow>
                    <CleanTableHead>User</CleanTableHead>
                    {contextType === "organization" ? (
                      <>
                        <CleanTableHead>Roles</CleanTableHead>
                        <CleanTableHead>Business Units</CleanTableHead>
                      </>
                    ) : (
                      <>
                        <CleanTableHead>Role</CleanTableHead>
                        <CleanTableHead>Assigned Date</CleanTableHead>
                      </>
                    )}
                    {canManage && <CleanTableHead>Actions</CleanTableHead>}
                  </CleanTableRow>
                </CleanTableHeader>
                <CleanTableBody>
                  {filteredUsers.map((user) => (
                    <CleanTableRow key={user.id}>
                      <CleanTableCell>
                        <div className="flex items-center gap-3">
                          <Avatar>
                            <AvatarImage src={user.profile_pic} />
                            <AvatarFallback>
                              {getUserInitials(user.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="font-medium">{user.name}</div>
                            <div className="text-sm text-muted-foreground">
                              {user.email}
                            </div>
                            {user.title && (
                              <div className="text-xs text-muted-foreground">
                                {user.title}
                              </div>
                            )}
                          </div>
                        </div>
                      </CleanTableCell>

                      {contextType === "organization" ? (
                        <>
                          <CleanTableCell>
                            <div className="flex flex-wrap gap-1">
                              {user.roles.map((role, index) => (
                                <Badge
                                  key={index}
                                  variant={getRoleBadgeVariant(role.type)}
                                  className="text-xs font-normal"
                                >
                                  {getRoleDisplayName(role.type)}
                                </Badge>
                              ))}
                            </div>
                          </CleanTableCell>
                          <CleanTableCell>
                            <div className="space-y-1">
                              {user.businessUnits.map((bu, index) => (
                                <div key={index}>
                                  <span className="text-sm">{bu.name}</span>
                                </div>
                              ))}
                              {user.businessUnits.length === 0 && (
                                <span className="text-sm text-muted-foreground">
                                  Organization only
                                </span>
                              )}
                            </div>
                          </CleanTableCell>
                        </>
                      ) : (
                        <>
                          <CleanTableCell>
                            <Badge
                              variant={
                                user.role === "business_unit_head"
                                  ? "default"
                                  : "secondary"
                              }
                              className="text-xs font-normal"
                            >
                              {user.roleDisplay}
                            </Badge>
                          </CleanTableCell>
                          <CleanTableCell>
                            <span className="text-sm text-gray-600">
                              {new Date(user.assignedAt).toLocaleDateString()}
                            </span>
                          </CleanTableCell>
                        </>
                      )}

                      {canManage && (
                        <CleanTableCell>
                          <div className="flex items-center gap-2">
                            {contextType === "organization" ? (
                              // Organization context - show remove buttons for each role
                              user.roles.map((role, roleIndex) => {
                                if (
                                  role.type === "business_unit_head" ||
                                  role.type === "business_user"
                                ) {
                                  return (
                                    <Tooltip key={roleIndex}>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() =>
                                            onRemoveUser(
                                              user.id,
                                              role.businessUnit.id,
                                              role.type
                                            )
                                          }
                                          className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        Remove from {role.businessUnit.name}
                                      </TooltipContent>
                                    </Tooltip>
                                  );
                                }
                                return null;
                              })
                            ) : (
                              // Business unit context - show single remove button
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      onRemoveUser(user.id, user.role)
                                    }
                                    className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Remove User</TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        </CleanTableCell>
                      )}
                    </CleanTableRow>
                  ))}
                </CleanTableBody>
              </CleanTable>
            </TooltipProvider>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default UserTable;
